import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Accordion from '../Accordion/Accordion';
import AccordionItem from '../Accordion/AccordionItem';
import Address from '../Address/Address';
import { format, parseISO } from 'date-fns';
import { assemblyTestStatusStyle } from './assemblyTestStatus';
import { constantToLabel, ucFirst } from '@ecosconnect/core/src/helpers/string';
import useMode from '../../hooks/useMode';
import StatusChip from '../Status/StatusChip';
import { BackflowAssembly } from './types';
import { waterServiceStatusStyle } from '../WaterService/waterServiceStatus';
import { Flex, Text } from 'rebass';
import DataTable from '../Table/DataTable';
import customerColumns from './AssemblyThirdPane.columns';
import { useHistory } from 'react-router-dom';
import { Customer } from 'components/Customer/types';

type AssemblyDetailProps = {
  assembly?: BackflowAssembly;
};

const AssemblyDetail: React.FC<AssemblyDetailProps> = ({ assembly }) => {
  const mode = useMode();
  const history = useHistory();

  // TODO: Use complianceReason?
  const status = assembly?.lastTest?.finalResult;
  const lastTestStatusChip = assembly?.lastTest ? (
    <StatusChip status={status === 'PASS' ? 'success' : 'error'}>
      {status ? constantToLabel(status) : 'Installation'}
    </StatusChip>
  ) : (
    <></>
  );

  const customers: Customer[] = useMemo(() => {
    if (!assembly?.serviceAddresses) return [];

    const customerMap = new Map<string, Customer>();

    assembly.serviceAddresses.forEach((serviceAddress) => {
      if (
        serviceAddress?.customer &&
        !customerMap.has(serviceAddress.customer.id)
      ) {
        customerMap.set(serviceAddress.customer.id, serviceAddress.customer);
      }
    });

    return Array.from(customerMap.values());
  }, [assembly?.serviceAddresses]);

  const customerIds = useMemo(() => {
    return customers && customers.length
      ? customers.map((customer) => customer.id)
      : [];
  }, [customers]);

  return (
    <>
      <Accordion title="Summary">
        {mode === 'water-supplier' && assembly?.customerName && (
          <AccordionItem
            kind={assembly.customerId ? 'link' : 'text'}
            label="Customer"
            value={assembly.customerName}
            href={
              assembly.customerId
                ? `/${mode}/customers/${assembly.customerId}`
                : undefined
            }
          />
        )}
        {assembly?.serialNumber && (
          <AccordionItem label="Serial" value={assembly.serialNumber} />
        )}
        {assembly && (
          <AccordionItem
            label="Model"
            value={`${assembly.model?.backflowAssemblyMake.name} ${assembly.model?.name}`}
          />
        )}
        {assembly?.type && (
          <AccordionItem
            label="Type"
            value={assembly.type.replace(/_/g, ' ')}
          />
        )}
        {assembly?.size && (
          <AccordionItem label="Size" value={assembly.size + '"'} />
        )}
        {assembly?.hazardType && (
          <AccordionItem
            title={assembly.hazardLevel}
            kind={assembly.hazardLevel === 'HIGH' ? 'dot' : undefined}
            status={assembly.hazardLevel === 'HIGH' ? 'error' : 'default'}
            label="Hazard"
            value={ucFirst(assembly.hazardType)}
          />
        )}
        {assembly?.protectionType && (
          <AccordionItem
            label="Protection Type"
            value={constantToLabel(assembly.protectionType)}
          />
        )}
        {assembly?.location && (
          <AccordionItem label="Location" value={ucFirst(assembly.location)} />
        )}
        {assembly?.installDate && (
          <AccordionItem
            label="Install Date"
            value={
              assembly?.installDate && !assembly.installDate.startsWith('00')
                ? format(parseISO(assembly.installDate), 'M/d/yyyy')
                : '-'
            }
          />
        )}
      </Accordion>
      {assembly?.waterService && (
        <Accordion title="Water Service">
          {mode === 'water-supplier' && (
            <>
              <AccordionItem
                kind="link"
                label="Service"
                value={assembly.waterService.siteNumber}
                href={`/${mode}/water-services/${assembly.waterService?.id}`}
              />
              <AccordionItem
                kind="chip"
                status={waterServiceStatusStyle(assembly.waterService.status)}
                label="Status"
                value={constantToLabel(assembly.waterService.status)}
              />
            </>
          )}
          {assembly?.waterSupplierProfile && (
            <AccordionItem
              label="Water Supplier"
              value={assembly.waterSupplierProfile.name}
            />
          )}
          {assembly?.waterService.pin && (
            <AccordionItem label="TRAC" value={assembly.waterService.pin} />
          )}
          {assembly?.waterService.meterNumber && (
            <AccordionItem
              label="Meter #"
              value={assembly.waterService.meterNumber}
            />
          )}
          {assembly && (
            <AccordionItem
              label="Address"
              value={<Address address={assembly.address} />}
            />
          )}
        </Accordion>
      )}
      <Accordion title="Testing" icon={lastTestStatusChip}>
        <AccordionItem
          kind={assembly?.lastTest ? 'link' : 'text'}
          label="Last Tested"
          value={
            assembly?.lastTestDate && !assembly.lastTestDate.startsWith('00')
              ? format(parseISO(assembly.lastTestDate), 'M/d/yyyy')
              : '-'
          }
          href={
            assembly?.lastTest
              ? `/${mode}/test-reports/${assembly.lastTest.jobId}`
              : undefined
          }
        />
        <AccordionItem
          label="Test Anniversary"
          value={
            assembly?.testAnniversary &&
            !assembly.testAnniversary.startsWith('00')
              ? format(parseISO(assembly.testAnniversary), 'MMM do')
              : '-'
          }
        />
        {assembly && (
          <AccordionItem
            kind="chip"
            status={assemblyTestStatusStyle(assembly.testStatus)}
            label="Test Status"
            value={constantToLabel(assembly.testStatus)}
          />
        )}
      </Accordion>
      {mode === 'company' && (
        <Accordion title="Customers">
          {customers && customers.length ? (
            <Flex maxHeight={350}>
              <DataTable
                columns={customerColumns}
                itemIds={customerIds}
                items={customers}
                onRowClick={(item: Customer) => {
                  if (item.id) {
                    history.push(`/${mode}/customers/${item.id}`);
                  }
                }}
              />
            </Flex>
          ) : (
            <Text variant="body2" color="fieldBorderHover">
              There are no customers associated with this assembly.
            </Text>
          )}
        </Accordion>
      )}
      {assembly?.lastNoticeSent && (
        <Accordion title="Last Notice Sent">
          <AccordionItem
            kind="link"
            label="Notice"
            value={assembly.lastNoticeSent.notice.type}
            href={`/${mode}/notices/${assembly.lastNoticeSent?.notice.id}`}
          />
          <AccordionItem
            label="Date"
            value={format(assembly.lastNoticeSent.created, 'M/d/yyyy')}
          />
        </Accordion>
      )}
    </>
  );
};

AssemblyDetail.propTypes = {
  assembly: PropTypes.any,
};

export default AssemblyDetail;
